// What the factory wants moved, and what moving it is worth.
//
// The industrial layer's brief to the chess layer. Computed **once per turn**,
// **frozen**, and handed over — the chess half then scores moves against it
// without ever asking the factory another question.
//
// Three reasons it is shaped that way, and each is a bug that already
// happened or was one step away:
//
// - **It is a fact about material, not about pieces.** "Where should this rook
//   go" is unanswerable from a piece type; the answer depends on what it is
//   holding, what grade that is, and which machine would actually take it. A
//   per-type weight table cannot say it. So the unit of the brief is a *flow*:
//   this material, from here, toward there, worth this much a square.
// - **It must not be recomputed per candidate move.** `scoreMove` runs on every
//   legal move of every candidate turn, and the old haul term called
//   `nearestDropOff` — which sweeps the board — inside that loop. Once per turn
//   is the same answer for a fraction of the cost, because the factory does not
//   change between two candidate moves of the same turn.
// - **It must not be mutated while the chess layer holds it.** A brief the
//   search could write to would make move scoring order-dependent, which is a
//   desync in a game whose whole determinism argument is "same state, same
//   plan". Frozen, and `guardPayloadSize` says so out loud if one ever starts
//   growing turn on turn instead of being rebuilt.
//
// What this replaces is a flat per-square constant. `closed * greed` priced
// moving one lump of scrap toward a foundry exactly as it priced moving twenty
// bars of diamond, which is how a rook carrying the game's entire refined
// output came to be worth marching at the enemy. The number here is the real
// one: how much value the load *gains* by getting a square closer to something
// that wants it — the same quantity `evaluate` already prices with
// `reachFactor`, so the two halves of the opponent finally agree about what a
// haul is worth.

import { type Color, fileOf, pieceColor, rankOf, type Side } from "src/engine/rules/board";
import { areEnemies, pieceAt } from "src/engine/rules/position";
import { unitAt, unitCount } from "src/engine/rules/industry/state";
import { acceptsUnit } from "src/engine/rules/industry/machines";
import type { GameState } from "src/engine/rules/commands";
import { consumerSquares, reachFactor, unitWorth } from "./factory";
import { guardPayloadSize, WEIGHTS, type Weights } from "./tuning";

/**
 * Why a flow exists, which is the difference between a fact and a plan.
 *
 * A **proposal** is re-derived from the board this turn: this piece holds
 * material, that machine would take it. A **plan** is the same statement with
 * *memory* — a job already under way, carried across turns by `ai/tasks.ts`,
 * which is the only layer in the opponent that remembers anything.
 *
 * They are one kind of thing and always were: `TaskKind` had exactly one
 * member, `"haul"`, saying the same sentence the haul flow says. What a task
 * adds is age, and age is what buys momentum — a job under way is worth more
 * than the same job proposed cold, which is the only thing that lets a
 * horizon-limited search finish anything.
 */
export type FlowKind = "proposal" | "plan";

/** One piece's load, and where the factory would like it to go. */
export interface Flow {
  kind: FlowKind;
  /** Turns this plan has been running. Zero for a fresh proposal. */
  age: number;
  /** The square holding it, at the moment the brief was taken. */
  subject: number;
  /** The machine it should reach. */
  toward: number;
  /** What is being carried, in centipawns. */
  value: number;
  /**
   * Centipawns gained per square closed on `toward`.
   *
   * The *marginal* value, not the total: material three squares from an oven
   * is already worth most of what it will be worth, and what a move can change
   * is only the difference. Taking the total instead would pay a piece for
   * holding something rather than for moving it.
   */
  worth: number;
}

/**
 * The factory's standing instructions for this turn. Immutable.
 *
 * `bySubject` is the index the scoring loop uses — a map lookup per move
 * rather than a scan of the list, because the list is walked once per
 * candidate move and there can be hundreds of those.
 */
export interface Brief {
  readonly flows: readonly Flow[];
  /**
   * What each kind of goal is contributing, in centipawns per square.
   *
   * A diagnostic and a tuning aid: "which goal is driving this turn" is not
   * answerable from a single summed score, and a plan layer that is quietly
   * contributing nothing looks exactly like one that is working.
   */
  readonly byKind: Readonly<Record<FlowKind, number>>;
  readonly bySubject: ReadonlyMap<number, Flow>;
  /** Anything the brief noticed about its own shape. Empty is the normal case. */
  readonly warnings: readonly string[];
}

const EMPTY: Brief = Object.freeze({
  flows: Object.freeze([]),
  byKind: Object.freeze({ proposal: 0, plan: 0 }),
  bySubject: new Map(),
  warnings: Object.freeze([])
});

/** What a piece is carrying that could go somewhere, in centipawns. */
function loadOf(state: GameState, sq: number, w: Weights): number {
  let value = 0;
  for (const store of ["in", "out"] as const) {
    for (let i = 0; i < unitCount(state.industry, sq, store); i++) {
      value += unitWorth(unitAt(state.industry, sq, i, store), w);
    }
  }
  return value;
}

/**
 * Where this load should go: the nearest machine that would actually take it.
 *
 * "Actually" is the load-bearing word and it is asked per *unit*, through
 * `acceptsUnit` — the one place that knows a refinery refuses what it has
 * already cooked, a foundry refuses material below its recipe's grade, and an
 * idle foundry refuses everything. Routing toward a machine that cannot use
 * the material strands it for the rest of the game, and the piece would walk
 * the whole way to find that out.
 *
 * **Never the square it is standing on.** A machine is at distance zero from
 * itself, so a refinery holding its own finished output reads as perfectly
 * well placed while being exactly the material with nowhere to go — the same
 * trap `needsMoving` documents, arriving through a different door.
 */
function sinkFor(
  state: GameState,
  from: number,
  side: Side,
  consumers: readonly number[]
): number {
  let best = -1;
  let bestAway = Infinity;
  for (const at of consumers) {
    if (at === from) continue;
    let wanted = false;
    for (const store of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(state.industry, from, store); i++) {
        const unit = unitAt(state.industry, from, i, store);
        if (acceptsUnit(state.position, state.industry, at, side, unit)) {
          wanted = true;
          break;
        }
      }
      if (wanted) break;
    }
    if (!wanted) continue;
    const away = Math.max(
      Math.abs(fileOf(at) - fileOf(from)),
      Math.abs(rankOf(at) - rankOf(from))
    );
    if (away < bestAway) {
      bestAway = away;
      best = at;
    }
  }
  return best;
}

/**
 * Take the factory's brief for this turn.
 *
 * Call once, at the top of planning, and pass the result down. Calling it per
 * move is correct and defeats the purpose.
 */
export function materialBrief(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS,
  /**
   * What this side is already in the middle of (`ai/tasks.ts`).
   *
   * **This is where plans enter the payload the chess layer scores against.**
   * Without it the brief is a snapshot with no memory: it can say "this ore
   * wants to go there" and cannot say "we are three turns into building the
   * thing that will move it", so a long lane is never worth starting because
   * its payoff is past the rollout horizon. A task backing a flow is what
   * turns a fact into an intention.
   */
  tasks: readonly { subject: number; target: number; age: number }[] = []
): Brief {
  const consumers = consumerSquares(state, side);
  if (consumers.length === 0) return EMPTY;

  const flows: Flow[] = [];
  // Square order, never Map order: two clients deriving one board have to
  // agree, and insertion order is a thing they would agree on only by luck.
  const carrying = [...state.position.board.keys()].sort((a, b) => a - b);
  for (const sq of carrying) {
    const piece = pieceAt(state.position, sq);
    if (!piece || areEnemies(state.position, side, pieceColor(piece))) continue;
    // A machine's own square is not a destination for its own output, and a
    // piece with nothing in hand has nothing to move.
    const value = loadOf(state, sq, w);
    if (value <= 0) continue;

    const toward = sinkFor(state, sq, side, consumers);
    if (toward < 0) continue;

    const away = Math.max(
      Math.abs(fileOf(toward) - fileOf(sq)),
      Math.abs(rankOf(toward) - rankOf(sq))
    );
    // Already touching it: the tick hands it over without anybody planning a
    // move, so paying for the last square would walk pieces onto machines for
    // nothing.
    if (away <= 1) continue;

    // The marginal gain of one square, in the same centipawns `evaluate`
    // prices the material in. `reachFactor` is a hyperbola, so this is largest
    // where the load is nearly home and never reaches zero however far out it
    // is — which is what stops a distant piece having no reason to take the
    // first step. Measured before the term existed: a queen nine squares from
    // an oven moved one square in ten turns.
    const here = reachFactor(sq, [toward], w);
    const nearer = 1 / (1 + w.reachFalloff * (away - 1));
    // A task already running on this piece, for this destination, makes this
    // flow a *plan* rather than a proposal — and momentum is what a plan is
    // worth over the same job proposed cold. Matched on both ends: a task
    // about this piece heading somewhere else is a different intention, and
    // crediting it here would be momentum for work nobody is doing.
    const plan = tasks.find((task) => task.subject === sq && task.target === toward);
    const momentum = plan ? 1 + w.momentum * Math.min(plan.age, w.momentumCap) : 1;
    flows.push({
      kind: plan ? "plan" : "proposal",
      age: plan?.age ?? 0,
      subject: sq,
      toward,
      value,
      worth: value * (nearer - here) * momentum
    });
  }

  const bySubject = new Map(flows.map((flow) => [flow.subject, flow]));
  const byKind = { proposal: 0, plan: 0 };
  for (const flow of flows) byKind[flow.kind] += flow.worth;
  const warnings = guardPayloadSize("industrial brief", flows);

  // Frozen, because the chess layer holds it across every candidate move of
  // the turn and a brief that could be written to would make move scoring
  // depend on the order moves were scored in.
  return Object.freeze({
    flows: Object.freeze(flows),
    byKind: Object.freeze(byKind),
    bySubject,
    warnings: Object.freeze(warnings)
  });
}

/** What this move is worth to the factory. Zero for a piece carrying nothing. */
export function flowGain(brief: Brief, from: number, to: number): number {
  const flow = brief.bySubject.get(from);
  if (!flow) return 0;
  const before = Math.max(
    Math.abs(fileOf(flow.toward) - fileOf(from)),
    Math.abs(rankOf(flow.toward) - rankOf(from))
  );
  const after = Math.max(
    Math.abs(fileOf(flow.toward) - fileOf(to)),
    Math.abs(rankOf(flow.toward) - rankOf(to))
  );
  return (before - after) * flow.worth;
}

/** Total value the factory has in transit — a summary, for reporting. */
export function inTransit(brief: Brief): number {
  let total = 0;
  for (const flow of brief.flows) total += flow.value;
  return total;
}
